<template>
    <SectionContentLayout
      :state="compose_controller.state.events['fetch_message']"
      error_text="composer.fetch_message_error"
      @errorRetry="onErrorRetry()"
    >
      <template #close>
          <ActionButton
            @click="onActionClose()"
            class="close"
            icon="fas fa-times-circle"
            v-tooltip='"global.actions.close"'
          />
      </template>

      <template #header>
        <ContentTitle
          title="composer.title"
        />

        <div v-show="draft_saved_visible"
          class="draft-saved"
        >
          <i class="fas fa-check"></i>
          <span>
            {{ Language.say('composer.draft_saved') }}
          </span>
        </div>

      </template>

      <template #actions_success>
          <slot v-if="compose_controller.state.events['send_message'] !== 'loading'">
            <ActionButton v-if="route.name === 'popup-compose'"
              @click="onActionPopIn()"
              v-tooltip='"messages.actions.open_inline"'
              icon='fas fa-external-link-alt'
              class="flip"
            />

            <ActionButton v-else
              @click="onActionPopOut()"
              v-tooltip='"messages.actions.open_popup"'
              icon='fas fa-external-link-alt'
              class="popup"
              :gtm="{
                module : 'compose',
                feature: 'compose_message',
                block  : 'top_actions',
                action : 'open_popup'
              }"
            />

            <ActionButton
              @click="onActionSaveDraft()"
              v-tooltip='"messages.actions.save_draft"'
              icon='fas fa-save'
              :disabled="compose_controller.compose.attachments_upload.count"
              :gtm="{
                module : 'compose',
                feature: 'compose_message',
                block  : 'top_actions',
                action : 'save_draft'
              }"
            />
          </slot>

          <ActionButton
            @click="onActionSend()"
            icon='fas fa-paper-plane'
            text="composer.footer.send"
            :disabled="compose_controller.state.events['send_message'] === 'loading' || compose_controller.compose.attachments_upload.count"
            :gtm="{
              module : 'compose',
              feature: 'compose_message',
              block  : 'top_actions',
              action : 'send_message'
            }"
          />
        </template>

        <template #content_success>
            <form autocomplete="off">
                <div class="compose-details">
                    <ComposeFrom v-if="identities_controller.identities.count > 1"
                      @fromChange="onFromChange"
                      :compose="compose_controller.compose"
                      :identities_collection="identities_controller.identities"
                    />

                    <div class="control-group"
                        v-drag-drop="{
                            drop_data: {
                              to:'to',
                            },
                            hover_events: ['drag-contact-boxes'],
                            drop_suffix: 'compose'
                          }"
                        >
                      <div class="control">
                          <label
                            @click="onContactsToModalOpen('persons')"
                            role="button"
                            for="to"
                            v-tooltip='"composer.details.add_contacts"'
                          >
                              {{Language.say('composer.details.to')}}
                          </label>
                          <ContactsInputBoxes
                            ref="contacts_to_component"
                            @inputFocus="onRecipientsFocus('to')"
                            contact_input_id="to"
                            :input_focus="!route.query.action || route.query.action.startsWith('forward')"
                            :contacts_controller="contacts_controller"
                            :contacts_boxes_collection="compose_controller.compose.to"
                          >
                          </ContactsInputBoxes>

                          <ActionButton v-show="contacts_frequent_position === 'to'"
                            @click="onContactsToModalOpen('frequent')"
                            v-tooltip='"composer.details.frequent_contacts"'
                            class="clear"
                            icon='far fa-user'
                            tabindex="-1"
                          />

                          <ActionButton v-show="!cc_input_visible"
                            @click="onShowMoreRecipients()"
                            v-tooltip='"composer.details.show_cc_bcc"'
                            class="clear"
                            text='composer.details.cc'
                            tabindex="-1"
                          />

                      </div>
                    </div>

                    <div class="control-group"
                        v-show="cc_input_visible"
                        v-drag-drop="{
                            drop_data: {
                              to:'cc',
                            },
                            hover_events: ['drag-contact-boxes'],
                            drop_suffix: 'compose'
                          }"
                        >
                      <div class="control">
                            <label
                              @click="onContactsCCModalOpen('persons')"
                              role="button"
                              for="cc"
                              v-tooltip='"composer.details.add_contacts"'
                            >
                                {{Language.say('composer.details.cc')}}
                            </label>
                            <ContactsInputBoxes
                              ref="contacts_cc_component"
                              @inputFocus="onRecipientsFocus('cc')"
                              contact_input_id="cc"
                              :contacts_controller="contacts_controller"
                              :contacts_boxes_collection="compose_controller.compose.cc"
                            >
                            </ContactsInputBoxes>

                            <ActionButton v-show="contacts_frequent_position === 'cc'"
                              @click="onContactsCCModalOpen('frequent')"
                              v-tooltip='"composer.details.frequent_contacts"'
                              class="clear"
                              icon='far fa-user'
                              tabindex="-1"
                            />
                        </div>
                    </div>
                    <div class="control-group" v-show="bcc_input_visible"
                        v-drag-drop="{
                            drop_data: {
                              to:'bcc',
                            },
                            hover_events: ['drag-contact-boxes'],
                            drop_suffix: 'compose'
                          }"
                        >
                        <div class="control">
                            <label
                              @click="onContactsBCCModalOpen('persons')"
                              role="button"
                              for="bcc"
                              v-tooltip='"composer.details.add_contacts"'
                            >
                                {{Language.say('composer.details.bcc')}}
                            </label>
                            <ContactsInputBoxes
                              ref="contacts_bcc_component"
                              @inputFocus="onRecipientsFocus('bcc')"
                              contact_input_id="bcc"
                              :contacts_controller="contacts_controller"
                              :contacts_boxes_collection="compose_controller.compose.bcc"
                            >
                            </ContactsInputBoxes>

                            <ActionButton v-show="contacts_frequent_position === 'bcc'"
                              @click="onContactsBCCModalOpen('frequent');"
                              v-tooltip='"composer.details.frequent_contacts"'
                              class="clear"
                              icon='far fa-user'
                              tabindex="-1"
                            />
                        </div>
                    </div>

                    <div class="control-group">
                        <div class="control">
                            <label for="subject">{{Language.say('composer.details.subject')}}</label>
                            <input id="subject" v-model="compose_controller.compose.subject" type="text" autocomplete="off">
                        </div>
                    </div>
                    <div class="control-group">
                        <div class="control">
                            <label for="priority">{{Language.say('composer.details.priority')}}</label>
                            <span class="select-wrapper">
                                <select id="priority" tabindex="0" v-model="compose_controller.compose.priority" >
                                    <option value="high">{{Language.say('composer.details.priority_high')}}</option>
                                    <option value="normal">{{Language.say('composer.details.priority_normal')}}</option>
                                    <option value="low">{{Language.say('composer.details.priority_low')}}</option>
                                </select>
                            </span>
                        </div>
                        <div class="control check-group">
                            <input type="checkbox" id="check1" tabindex="-1" v-model="compose_controller.compose.read_receipt" :checked="compose_controller.compose.read_receipt">
                            <label for="check1" tabindex="0">{{Language.say('composer.details.receipt')}}</label>
                        </div>
                        <span
                          @click="onAttachmentsOpen()"
                          class="button clear"
                          tabindex="0"
                          role= "button"
                        >
                          <i
                            class='fas fa-paperclip'
                            aria-hidden="true"
                          />
                          {{Language.say('composer.details.attachments')}}
                          <small>
                            <span v-if="compose_controller.compose.attachments.count || compose_controller.compose.attachments_upload.count"
                              class="counter"
                              :class="{'new' : compose_controller.compose.attachments.count, 'uploading' : compose_controller.compose.attachments_upload.count}"
                            >
                              {{compose_controller.compose.attachments.count + compose_controller.compose.attachments_upload.count}}
                            </span>
                          </small>
                        </span>
                    </div>
                </div>

                <HTMLEditor
                  ref="html_editor_component"
                  @inlineImagesDrop="onInlineImagesDrop"
                  @inlineAttachmentsDrop="onAttachmentsDrop"
                  @contentModified="onContentModified"
                  :image_size_limit="config.attachments.size_limit"
                />

                <ContentSaving v-if="compose_controller.state.events['send_message'] === 'loading'"
                  info_text="composer.sending_message"
                />

                <ConfirmModal v-if="subject_warning_visible"
                  text_cancel="global.actions.back"
                  @actionConfirm="onSubjectWarningConfirm"
                  @actionCancel="onSubjectWarningCancel"
                >
                  <template #modal_body>
                    <p>
                      {{Language.say('composer.subject_warning')}}
                    </p>
                  </template>
                </ConfirmModal>

            </form>
        </template>
    </SectionContentLayout>

    <ComposeAttachmentsModal v-if="attachments_modal_visible"
      @modalWindowClose="onAttachmentsClose"

      :compose_controller="compose_controller"
      :meo_cloud_controller="meo_cloud_controller"
    />

    <ContactsSelectModal v-if="contacts_modal_visible"
      @contactsSelectModalCancel="onContactsModalCancel"
      @contactsSelectModalSubmit="onContactsModalSubmit"
      :contacts_controller="contacts_controller"
      :selected_contacts="contacts_modal_selected"
      :starting_tab="contacts_modal_tab"
    >
    </ContactsSelectModal>

    <DragDrop
      ref="ref_dragdrop_contact"
      event_name="drag-contact-boxes"
      :icon="{single: 'fas fa-address-card'}"
      :message="{single: contacts_name_dragging}"
      :dragdrop_controller="DragDropController"
    />

    <template id="sapo-transfer" class="sapo-transfer">
        <div class="link-share" contenteditable=false style="width: 20rem; max-width: 100%; padding:10px; margin:10px 10px 10px 0px; background: #e4f1d2; font-family: arial; font-weight: bold; font-size: 14px; color: #222">
            <p class="share" style="display:block; margin-bottom: 5px; font-weight: 600;"></p>

            <div class="file-upload">
                <p>
                    <span class="file"></span>
                    <small class="file-upload-size" style="display: block; color: #555;"></small>
                </p>
                <p>
                    <span class="uploading"></span>
                    <span class="counter"></span>
                </p>

                <div class="progress-bar">
                    <span class="file"></span>
                    <span class="progress" style="width: 0%;"></span>
                </div>
            </div>

            <p class="file-share" style="display: none; margin:0">
                <a target="_blank" class="file-link" data-unmutable style="cursor: pointer; text-decoration: underline; color: #487400" rel="noopener noreferrer"> 
                    <strong class="file-name" style="display:block; text-decoration: underline; margin-bottom: 3px;"></strong>
                </a>
                <small style="display: block; color: #555;" class="file-size"></small>
                <span class="file-expiration" style="color: #555; font-weight: normal; display:block; margin-top: 5px; font-size: 12px"></span>
            </p>
      </div>
    </template>

    <template id="meo-cloud" class="meo-cloud">
        <div class="link-share" contenteditable=false style="width: 20rem; max-width: 100%; -webkit-user-modify: read-only; padding:10px; margin:10px 10px 10px 0px; background: #c6e8ed; font-family: arial; font-weight: bold; font-size: 14px; color: #222">
            <p class="share" style="display:block; margin-bottom: 5px; font-weight: 600;">
              Partilha MEOCloud
            </p>

            <p class="file-share" style="margin:0">
            </p>
      </div>
    </template>

      <template id="meo_cloud_links">
          <a target="_blank" class="file-link" data-unmutable style="cursor: pointer; text-decoration: underline; color: #487400" rel="noopener noreferrer">
            <strong class="file-name" style="display:block; text-decoration: underline; margin-bottom: 3px;"></strong>
          </a>
      </template>
</template>

<script setup>
  import {ref, nextTick, reactive}                 from 'vue';
  import {onMounted, onUnmounted, onBeforeUnmount} from 'vue';
  import {defineProps}                             from 'vue';
  import {useRoute}                                from 'vue-router';
  import ComposeFrom                               from '../Partials/ComposeFrom.vue';
  import ComposeAttachmentsModal                   from '../Modals/ComposeAttachmentsModal.vue';
  import SectionContentLayout                      from '@/vue/Layouts/Sections/SectionContentLayout.vue';
  import ActionButton                              from '@/vue/Elements/Buttons/ActionButton.vue';
  import ContentTitle                              from '@/vue/Elements/Content/ContentTitle.vue';
  import ContentSaving                             from '@/vue/Elements/Content/ContentSaving.vue';
  import HTMLEditor                                from '@/vue/Elements/Tools/HTMLEditor/HTMLEditor.vue';
  import DragDrop                                  from '@/vue/Elements/Tools/DragDrop.vue';
  import ContactsInputBoxes                        from '@/vue/Views/Contacts/Partials/ContactsInputBoxes.vue';
  import ContactsSelectModal                       from '@/vue/Views/Contacts/Modals/ContactsSelectModal.vue';
  import ConfirmModal                              from '@/vue/Views/Webmail/Modals/ConfirmModal.vue';
  import MessagesOptionsController                 from '@/js/Settings/MessagesOptions/MessagesOptionsController';
  import MeoCloudController                        from "@/js/Compose/MeoCloudController";
  import Language                                  from '@/js/Shared/Language/Language';
  import PopupController                           from '@/js/Webmail/PopupController';
  import DragDropController                        from '@/js/Webmail/DragDrop/DragDropController';
  import SAPOMailConfig                            from '@/SAPOMailConfig';

  const props = defineProps({
    webmail_view          : {},
    popup_view            : {},
    identities_controller : {},
    contacts_controller   : {},
    state_consumer        : {},
    compose_controller    : {}
  })

  const message_options_controller = MessagesOptionsController;
  const meo_cloud_controller       = reactive(new MeoCloudController());
  const html_editor_component      = ref();
  const contacts_to_component      = ref();
  const contacts_cc_component      = ref();
  const contacts_bcc_component     = ref();
  const contacts_modal_visible     = ref();
  const contacts_modal_tab         = ref();
  const contacts_modal_selected    = ref();
  const contacts_frequent_position = ref('to');
  const contacts_name_dragging     = ref(null);
  const draft_saved_visible        = ref(false);
  const subject_warning_visible    = ref();
  const attachments_modal_visible  = ref();
  const active_contacts_component  = ref()
  const cc_input_visible           = ref();
  const bcc_input_visible          = ref();
  const from_focus                 = ref(false);
  const config                     = SAPOMailConfig.client.compose;
  const route                      = useRoute();
  const auto_save_draft            = ref();
  const close_action               = ref('close');
  const ref_dragdrop_contact       = ref();
  const file_share_elements        = ref({});
  const file_share_elements_count  = ref(0);
  const file_share_contents        = {
    'sapo-transfer' : {
      'share'       : Language.say('composer.transfer_share'),
      'uploading'   : Language.say('composer.transfer_uploading'),
      'available'   : Language.say('composer.transfer_available_until'),
    }
  }

  onMounted(async () => {
    props.compose_controller.broadcaster.on('upload-attachment-inline-finished', onUploadInlineFinished);
    props.compose_controller.broadcaster.on('file-share-upload-started'        , onUploadFileShareStarted);
    props.compose_controller.broadcaster.on('file-share-upload-progress'       , onUploadFileShareProgress);
    props.compose_controller.broadcaster.on('file-share-upload-finished'       , onUploadFileShareFinished);
    props.compose_controller.broadcaster.on('file-share-upload-aborted'        , onUploadTransferAborted);
    DragDropController.broadcaster.on('drag-contact-boxes-start', onDragStart)
    DragDropController.broadcaster.on('drag-contact-boxes-drop-compose', onDragDrop)
    window.addEventListener('beforeunload', onWindowBeforeUnload);

    await fetchMessageData();
    props.compose_controller.state.on('fetch_message', 'success', onLoad);
    setComposeType();
  })

  onBeforeUnmount(() => {
    clearInterval(auto_save_draft.value);

    if (close_action.value === 'close') {
      onActionUnmountSaveDraft();
    }
  })

  onUnmounted(() => {
    props.compose_controller.broadcaster.destroy('upload-attachment-inline-finished', onUploadInlineFinished);
    props.compose_controller.broadcaster.destroy('file-share-upload-started'        , onUploadFileShareStarted);
    props.compose_controller.broadcaster.destroy('file-share-upload-progress'       , onUploadFileShareProgress);
    props.compose_controller.broadcaster.destroy('file-share-upload-finished'       , onUploadFileShareFinished);
    props.compose_controller.broadcaster.destroy('file-share-upload-aborted'        , onUploadTransferAborted);
    props.compose_controller.state.destroy('fetch_message', 'success', onLoad);

    window.removeEventListener('beforeunload', onWindowBeforeUnload);
    DragDropController.broadcaster.destroy('drag-contact-boxes-start', onDragStart)
    DragDropController.broadcaster.destroy('drag-contact-boxes-drop-compose', onDragDrop)
  })

  const onErrorRetry = async () => {
    await fetchMessageData();
  }

  const onLoad = async () => {
    await nextTick();
    await nextTick();
    if (props.compose_controller.compose.identity.bccs.length) {
      props.compose_controller.compose.bcc.addPersonsByArray(props.compose_controller.compose.identity.bccs);
    }

    if (props.state_consumer.peek('contacts_to_recipients')) {
      props.compose_controller.compose.to.addItems(props.state_consumer.consume('contacts_to_recipients'));
    }

    if (props.compose_controller.compose.message) {
      html_editor_component.value.editor.insertHTML(props.compose_controller.compose.message);
    }

    if (props.compose_controller.compose.bcc.count || props.compose_controller.compose.cc.count) {
      setCCInputVisibility(true);
      setBCCInputVisibility(true);
    }

    if (message_options_controller.options.request_mdn) {
      setComposeReadReceipt(true);
    }

    if (props.compose_controller.compose.identity.signature_html && !props.compose_controller.compose.signature && route.query.action !== 'draft') {
      setSignature(props.compose_controller.compose.identity.signature_html, true);
    }

    if (config.auto_save_draft.enabled === true) {
      setComposeMessage();
      props.compose_controller.compose.generateHash();
      props.compose_controller.compose.setHashDraft();
      setAutoSaveDraft();
    }

    html_editor_component.value.editor.squire.moveCursorToStart();
  }

  const onWindowBeforeUnload = (event) => {
    if (close_action.value === 'send_message') {
      return;
    }

    setComposeMessage();
    props.compose_controller.compose.generateHash();

    if (props.compose_controller.compose.setHashDraft() === false) {
      return;
    }

    event.returnValue = "CHANGES_NOT_SAVED";
  }

  const onActionClose = () => {
    close('close');
  }

  const onActionPopOut = () => {
    setComposeMessage();
    forceRecipientsInputs();
    openComposePopup();
    close('open_popup')
  }

  const onActionPopIn = () => {
    setComposeMessage();
    forceRecipientsInputs();
    props.state_consumer.set('compose_controller', props.compose_controller);
    props.popup_view.routeTo('/compose');
    closeComposePopup();
  }

  const onFromChange = (identity) => {
    props.compose_controller.compose.bcc.deletePersonsByEmail(props.compose_controller.compose.identity.bccs);
    props.compose_controller.compose.setIdentity(identity);

    if (props.compose_controller.compose.identity.bccs.length) {
      props.compose_controller.compose.bcc.addPersonsByArray(props.compose_controller.compose.identity.bccs);
      setBCCInputVisibility(true);
    }

    removeSignature();
    setSignature(props.compose_controller.compose.identity.signature_html);
  }

  const onRecipientsFocus = (recipient) => {
    setContactsFrequentPosition(recipient);
  }

  const onShowMoreRecipients = () => {
    setCCInputVisibility(true);
    setBCCInputVisibility(true);
  }

  const onContactsToModalOpen = (tab) => {
    setContactsModalVisibility(true);
    setContactsModalTab(tab);
    setActiveContactsComponent(contacts_to_component.value);
    setContactsModalSelected(props.compose_controller.compose.to);
  }

  const onContactsCCModalOpen = (tab) => {
    setContactsModalVisibility(true);
    setContactsModalTab(tab);
    setActiveContactsComponent(contacts_cc_component.value);
    setContactsModalSelected(props.compose_controller.compose.cc);
  }

  const onContactsBCCModalOpen = (tab) => {
    setContactsModalVisibility(true);
    setContactsModalTab(tab);
    setActiveContactsComponent(contacts_bcc_component.value);
    setContactsModalSelected(props.compose_controller.compose.bcc);
  }

  const onContactsModalSubmit = (contacts) => {
    active_contacts_component.value.setContacts(contacts.index);
    setContactsModalVisibility(false);
  }

  const onContactsModalCancel = () => {
    setContactsModalVisibility(false);
  }

  const onAttachmentsOpen = async () => {
    setAttachmentsModalVisibility(true);
  }

  const onAttachmentsClose = async () => {
    setAttachmentsModalVisibility(false);
  }

  const onInlineImagesDrop = async (images) => {
    props.compose_controller.addInlineImages(images);
  }

  const onAttachmentsDrop = async (files) => {
    props.compose_controller.addAttachments(files);
  }

  const onUploadFileShareStarted = (data) => {
    let template = setFileShareElement(
      data.id,
      html_editor_component.value.editor.insertTemplate(data.target, data.id),
    );

    setFileShareInitialContents(data);
  }

  const onContentModified = async () => {
    if (file_share_elements_count.value > 0) {
      let templates = html_editor_component.value.editor.getRemovedTemplates(file_share_elements.value);
      props.compose_controller.compose.attachments.deleteItems(templates);

      for (let i = 0; i < templates.length; i++) {
        deleteFileShareElement(templates[i].id);
      }
    }
  }

  const onUploadFileShareProgress = (data) => {
    setFileShareProgressContents(data);
  }

  const onUploadFileShareFinished = (data) => {
    setFileShareFinishedContents(data);
  }

  const onUploadTransferAborted = (id) => {
    html_editor_component.value.editor.removeElement(id);
    deleteFileShareElement(id);
  }


  const onUploadInlineFinished = (attachment) => {
    if (!attachment) {
      return;
    }

    let image = document.getElementById(attachment.related_id);
    image.setAttribute('src', attachment.preview_url);
    image.setAttribute(attachment.related[0], attachment.related[1]);
  }

  const onActionAutoSaveDraft = async () => {
    setComposeMessage();
    props.compose_controller.compose.generateHash();

    if (props.compose_controller.compose.setHashDraft() === false) {
      return;
    }

    if (await props.compose_controller.saveAutoDraft() === true) {
      setDraftSavedVisibility(true);
      setAutoSaveDraftAutoHide(2000);
    }
  }

  const onActionUnmountSaveDraft = async () => {
    if (props.compose_controller.state.events['fetch_message'] !== 'success') {
      return;
    }

    forceRecipientsInputs();
    setComposeMessage();
    props.compose_controller.compose.generateHash();

    if (props.compose_controller.compose.setHashDraft() === false) {
      return;
    }

    await props.compose_controller.saveDraft();
  }

  const onActionSaveDraft = async () => {
    forceRecipientsInputs();
    setComposeMessage();

    if (await props.compose_controller.saveDraft() === true) {
      close('save_draft');
      return;
    }
  }

  const onActionSend = async () => {
    forceRecipientsInputs();
    setComposeMessage();

    if (!props.compose_controller.compose.subject) {
      setSubjectWarningVisible(true);
      return;
    }

    if (await props.compose_controller.sendMessage() === true) {
      close('send_message');
      return;
    }
  }

  const onSubjectWarningConfirm = async () => {
    setSubjectWarningVisible(false);

    if (await props.compose_controller.sendMessage() === true) {
      close('send_message');
      return;
    }
  }

  const onSubjectWarningCancel = async () => {
    setSubjectWarningVisible(false);
  }

  const onDragStart = (drag_item) => {
    drag_item.data.add(drag_item.initial_data.item, drag_item.initial_data.item.id)
    setContactsNameDragging(drag_item.initial_data.item.name);

    DragDropController.setDragOverlayElement(ref_dragdrop_contact.value.$el);
    DragDropController.setDragOverlayImage(drag_item.element_event);

    if(!cc_input_visible.value) {
      setCCInputVisibility(true);
      setBCCInputVisibility(true);
    }
  }

  const onDragDrop = (drag_item) => {
    if(drag_item.initial_data.from === drag_item.drop_data.to || !drag_item.drop_events.includes('drag-contact-boxes')){
      return;
    }

    setContactsNameDragging(null);
    props.compose_controller.compose.changeContactRecipient(
      drag_item.initial_data.item,
      drag_item.initial_data.from,
      drag_item.drop_data.to,
    );
  }

  const setAutoSaveDraft = () => {
    auto_save_draft.value = setInterval(onActionAutoSaveDraft, config.auto_save_draft.interval);
  }

  const forceRecipientsInputs = () => {
    contacts_to_component.value.forceInput();
    contacts_cc_component.value.forceInput();
    contacts_bcc_component.value.forceInput();
  }

  const fetchMessageData = async () => {
    return await props.compose_controller.fetchMessageByType(route.query.uid, route.query.mbox, route.query.action);
  }

  const setComposeMessage = () => {
    if (!html_editor_component.value.editor) {
      return;
    }

    props.compose_controller.compose.message = html_editor_component.value.editor.getContents();
  }

  const setComposeType = () => {
    props.compose_controller.compose.type = route.query.action;
  }

  const setComposeReadReceipt = (value) => {
    props.compose_controller.compose.read_receipt = value;
  }


  const setContactsFrequentPosition = (position) => {
    contacts_frequent_position.value = position;
  }

  const setActiveContactsComponent = (component) => {
    active_contacts_component.value = component;
  }

  const setContactsModalSelected = (tab) => {
    contacts_modal_selected.value = tab;
  }

  const setContactsModalTab = (contacts) => {
    contacts_modal_tab.value = contacts;
  }

  const setAttachmentsModalVisibility = (value) => {
    attachments_modal_visible.value = value;
  }

  const setSubjectWarningVisible = (value) => {
    subject_warning_visible.value = value;
  }

  const setContactsModalVisibility = (value) => {
    contacts_modal_visible.value = value;
  }

  const setCCInputVisibility = (value) => {
    cc_input_visible.value = value;
  }

  const setBCCInputVisibility = (value) => {
    bcc_input_visible.value = value;
  }

  const setDraftSavedVisibility = (value) => {
    draft_saved_visible.value = value;
  }

  const setContactsNameDragging = (value) => {
    contacts_name_dragging.value = value;
  }

  const setAutoSaveDraftAutoHide = (interval) => {
    setTimeout(() => {
      setDraftSavedVisibility(false);
    }, interval)
  }

  const removeSignature = () => {
    html_editor_component.value.editor.removeElement('sig-001');
  }

  const setSignature = (contents, add_line = false) => {
    if (!contents) {
      return;
    }

    if (add_line === true) {
      html_editor_component.value.editor.insertElement('<br />');
    }


    html_editor_component.value.editor.squire.moveCursorToStart();
    let signature = document.createElement('div');
    signature.setAttribute('id', 'sig-001');
    signature.innerHTML =  html_editor_component.value.editor.sanitizeHTML(contents);
    html_editor_component.value.editor.insertElement(signature, false);
    props.compose_controller.compose.signature = true;

  }

  const setFileShareElement = (id, element) => {
    file_share_elements.value[id] = element;
    file_share_elements_count.value++;
  }

  const deleteFileShareElement = (id) => {
    delete file_share_elements.value[id];
    file_share_elements_count.value--;
  }

  /**
   * This is the solution i had at the time, is it pretty? no, does it work? yes, so shut up!
   *
   * @fixme move this to an html editor template system
   */
  const setFileShareInitialContents = (data) => {
    let element = file_share_elements.value[data.id];
    element.querySelector(".share").textContent     = file_share_contents[data.target].share;
    element.querySelector(".uploading").textContent = file_share_contents[data.target].uploading;
  }

  /**
   * Still talking shit about it? get over it!
   *
   * @fixme move this to an editor template system
   */
  const setFileShareProgressContents = (data) => {
    file_share_elements.value[data.id].querySelector(".file").textContent             = data.events[data.current].file.name;
    file_share_elements.value[data.id].querySelector(".counter").textContent          = `(${data.current}/${data.total})`;
    file_share_elements.value[data.id].querySelector(".progress").style.width         = data.events[data.current].progress_percentage.value + '%';
    file_share_elements.value[data.id].querySelector(".file-upload-size").textContent = data.events[data.current].size_text;
  }

  /**
   * It's not going to change by itself!!! no matter how much you insult me!
   *
   * @fixme move this to an html editor template system
   */
  const setFileShareFinishedContents = (data) => {
    file_share_elements.value[data.attachment.id].querySelector(".file-upload").remove();
    file_share_elements.value[data.attachment.id].querySelector(".file-name").textContent = data.attachment.name;
    file_share_elements.value[data.attachment.id].querySelector(".file-expiration").textContent = file_share_contents[data.attachment.target].available + ' ' + data.metadata.expire;
    file_share_elements.value[data.attachment.id].querySelector(".file-link").setAttribute('href', data.metadata.download_link)
    file_share_elements.value[data.attachment.id].querySelector(".file-share").style.display = 'block';
    file_share_elements.value[data.attachment.id].querySelector(".file-size").textContent = data.attachment.size_text;
  }

  const setMeoCloudFileContents = (files) => {
    let links = document.getElementById('meo_cloud_links');

    let template = setFileShareElement(
      files.id,
      html_editor_component.value.editor.insertTemplate('meo-cloud', files.id),
    );

   files.public_links.forEach((file) => {
      let template = links.content.cloneNode(true);
      template.querySelector('.file-link').setAttribute('href', file.url);
      template.querySelector('.file-name').textContent = file.filename;
      file_share_elements.value[files.id].querySelector(".file-share").appendChild(template);
    });
  }

  const openComposePopup = () => {
    PopupController.open({
      path: '/compose',
      data: {
        compose_controller    : props.compose_controller,
        popup_view            : props.webmail_view,
      },
    })
  }

  const closeComposePopup = () => {
    PopupController.closeCurrentPopup();
  }

  const close = (action) => {
    close_action.value = action;

    if (route.name === 'popup-compose') {
      closeComposePopup();
    }

    props.webmail_view.routeBack();
  }
</script>